import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { doc, deleteDoc, writeBatch } from 'firebase/firestore';
import { v4 } from 'uuid';
import Swal from 'sweetalert2';
import QRCode from 'react-qr-code';
import { DB } from '~/services/firebase';

import Dashboard from '~/layouts/Dashboard';
import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';
import ITable from '~/interfaces/ITable';
import getValidationErrors from '~/utils/getValidationErrors';
import { useCartCompany } from '~/hooks/cartCompany';

const { REACT_APP_WEB_URL } = process.env;

const TablesForm: React.FC = () => {
  const { user, company } = useAuth();
  const { addToast } = useToast();
  const { tables, setTables, loadTables } = useCartCompany();

  const [number, setNumber] = useState(0);
  const [tableId, setTableId] = useState('');

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  const handleConfirmRemove = useCallback(
    async (uid: string) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do item?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        const items = [...tables];
        const findIndex = items.findIndex(find => find.uid === uid);

        if (findIndex !== -1) {
          await deleteDoc(doc(DB, 'tables', uid));

          items.splice(findIndex, 1);

          setTables(items);

          addToast({
            type: 'success',
            description: 'A mesa foi removida com sucesso.',
          });
        }
      }
    },
    [addToast, tables, setTables],
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const parseData = {
          number,
        };

        const schema = Yup.object().shape({
          number: Yup.number()
            .required('O campo é obrigatório')
            .min(1, 'O valor deve ser no mínimo 1'),
        });

        await schema.validate(parseData, {
          abortEarly: false,
        });

        const batch = writeBatch(DB);

        setLoading(true);

        const length = number - tables.length;

        const arr = Array.from({ length }, (_, i) => i + 1);
        const items: ITable[] = [];

        arr.forEach(item => {
          const uid = v4();

          const new_table: any = {
            uid,
            name: `${item + tables.length}`.padStart(2, '0'),
            user_id: user.uid,
            company_id: company.uid,
          };

          items.push(new_table);

          batch.set(doc(DB, 'tables', new_table.uid), new_table);
        });

        await batch.commit();

        setTables([...tables, ...items]);

        setNumber(0);

        addToast({
          type: 'success',
          description: 'Mesas cadastradas com sucesso!',
        });

        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
          console.log(parseErrors);
        }
      }
    },
    [user, company, addToast, number, tables, setTables],
  );

  return (
    <>
      <Dashboard containerMessage="col-xl-8 offset-xl-2 mb-4">
        <div className="row">
          <div className="col-xl-8 offset-xl-2 mb-4">
            <div className="nk-block nk-block-lg">
              <div className="nk-block-head">
                <div className="nk-block-head-content">
                  {/* <div className="nk-block-head-sub">
                    <Link className="back-to" to="/categories">
                      <em className="icon ni ni-arrow-left" />
                      <span>Voltar</span>
                    </Link>
                  </div> */}
                  <h4 className="title nk-block-title">Cadastrar Mesas</h4>
                  <div className="nk-block-des">
                    <p>Cadastre mesas, imprima os QR Codes e cole.</p>
                  </div>
                </div>
              </div>
              <div className="card card-bordered">
                <div className="card-inner">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="form-group">
                          <label className="form-label">Número de Mesas</label>
                          <div className="form-control-wrap">
                            <input
                              type="number"
                              className="form-control"
                              onChange={event => {
                                setNumber(parseInt(event.target.value));
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div
                        className="col-sm-6 d-flex"
                        style={{ alignItems: 'flex-end' }}
                      >
                        <div className="form-group">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading && (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              />
                            )}
                            {!loading && 'Cadastrar'}
                          </button>
                        </div>
                      </div>

                      <div className="col-12">
                        {errors.number && (
                          <span className="invalid">{errors.number}</span>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-8 offset-xl-2">
            <div className="nk-block-head nk-block-head-sm">
              <div className="nk-block-between">
                <div className="nk-block-head-content">
                  <h6 className="nk-block-title">Mesas</h6>
                </div>

                <Link
                  to="/print-qrcode"
                  target="_blank"
                  className="btn btn-primary"
                >
                  Imprimir QR Codes
                </Link>
              </div>
            </div>
            <div className="nk-block">
              <div className="card card-bordered">
                <div className="card-inner-group">
                  <div className="card-inner p-0">
                    <div className="nk-tb-list">
                      <div className="nk-tb-item nk-tb-head">
                        <div className="nk-tb-col">
                          <span>Número da mesa</span>
                        </div>

                        <div className="nk-tb-col nk-tb-col-tools">
                          <ul className="nk-tb-actions gx-1 my-n1">
                            <li className="mr-n1" />
                          </ul>
                        </div>
                      </div>

                      {tables.map(item => (
                        <div className="nk-tb-item" key={item.uid}>
                          <div
                            className="nk-tb-col"
                            onClick={() => setTableId(item.uid)}
                          >
                            <span className="tb-product">{item.name}</span>
                          </div>

                          <div className="nk-tb-col nk-tb-col-tools">
                            <ul className="nk-tb-actions gx-1 my-n1">
                              <li className="mr-n1">
                                <div className="dropdown">
                                  <Link
                                    to="#options"
                                    className="dropdown-toggle btn btn-icon btn-trigger"
                                    data-toggle="dropdown"
                                  >
                                    <em className="icon ni ni-more-h" />
                                  </Link>
                                  <div className="dropdown-menu dropdown-menu-right">
                                    <ul className="link-list-opt no-bdr">
                                      <li>
                                        <Link
                                          to="#remove"
                                          onClick={() =>
                                            handleConfirmRemove(item.uid)
                                          }
                                        >
                                          <em className="icon ni ni-trash" />
                                          <span>Excluir</span>
                                        </Link>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dashboard>

      {tableId && (
        <div className="modal-qrcode" onClick={() => setTableId('')}>
          <div className="d-flex align-items-center justify-content-center">
            <QRCode
              value={`${REACT_APP_WEB_URL}${company.app_domain}?table=${tableId}`}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default TablesForm;
